// cleanup_convert_to_typescript_file_CREATE_RECURRING_TRANSFERS

import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { FormContext } from '@glu/validation-react';

import BalanceDropdown, { accountBalServiceMapping } from 'components/balanceDrodown';
import usePlaceResolver from 'components/usePlaceResolver/usePlaceResolver';
import InfoDisplay from 'components/infoDisplay/InfoDisplay';
import { getPermittedAccounts } from 'api/transfers/service';
import LocaleContext from 'components/locale/LocaleContext';
import { createDropdown } from 'components/singleTransfer/SingleTransfer.utils';
import { ACCOUNT_TYPES } from 'constants.js';

import { useStyles } from './recurringTransfer.styles';

export const AccountsComponent = ({ creditAccountId, debitAccountId, readOnly }) => {
  const { dropdown, infoContainer, infoValue } = useStyles();
  const locale = useContext(LocaleContext);
  const services = useMemo(
    () => [
      getPermittedAccounts({
        showBalance: true
      })
    ],
    []
  );
  const { data, onChange: setValue } = useContext(FormContext);
  const {
    Placeholder,
    results: [permittedAccounts]
  } = usePlaceResolver({
    services
  });

  if (Placeholder) {
    return <Placeholder />;
  }

  const allAccounts = permittedAccounts.reduce(
    (acc, account) => {
      const { permissionType } = account;
      if ([ACCOUNT_TYPES.DEBIT, ACCOUNT_TYPES.BOTH].includes(permissionType)) {
        acc.debitAccounts.push(account);
      }

      if ([ACCOUNT_TYPES.CREDIT, ACCOUNT_TYPES.BOTH].includes(permissionType)) {
        acc.creditAccounts.push(account);
      }

      return acc;
    },
    { debitAccounts: [], creditAccounts: [] }
  );

  const debitAccountList = createDropdown(
    locale,
    allAccounts.debitAccounts,
    creditAccountId || data?.creditAccountId,
    ACCOUNT_TYPES.DEBIT
  );

  const creditAccountList = createDropdown(
    locale,
    allAccounts.creditAccounts,
    debitAccountId || data?.debitAccountId,
    ACCOUNT_TYPES.CREDIT
  );

  const getAccountDisplayName = ({ account, accountId, accountList }) => {
    const matchedAccount = accountList.filter(({ id }) => id === accountId);
    const hasMatchedAccount = matchedAccount.length > 0;
    const accountDetails = hasMatchedAccount ? matchedAccount[0] : account;
    const { balance, name, shortName } = accountDetails;

    return hasMatchedAccount ? (balance !== null ? name : shortName) : name;
  };

  return readOnly ? (
    <>
      <InfoDisplay
        containerClass={infoContainer}
        dataQa="debit-account"
        label={locale['common.fromAccount']}
        value={getAccountDisplayName({
          accountList: debitAccountList,
          accountId: data.debitAccountId,
          account: data.debitAccount[0]
        })}
        valueClass={infoValue}
      />
      <InfoDisplay
        containerClass={infoContainer}
        dataQa="credit-account"
        label={locale['common.toAccount']}
        value={getAccountDisplayName({
          accountList: creditAccountList,
          accountId: data.creditAccountId,
          account: data.creditAccount[0]
        })}
        valueClass={infoValue}
      />
    </>
  ) : (
    <>
      <BalanceDropdown
        className={dropdown}
        defaultSelectMessage={locale['common.select']}
        disabled={readOnly}
        forcePositionY
        htmlId="debitAccount"
        labelText={locale['common.fromAccount']}
        name="debitAccount"
        onChange={(name, value) => {
          setValue('debitAccountId', value?.[0]?.id, false);
        }}
        options={debitAccountList}
        service={accountBalServiceMapping.recurTransferDebit}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
      <BalanceDropdown
        className={dropdown}
        defaultSelectMessage={locale['common.select']}
        disabled={readOnly}
        forcePositionY
        getBalanceLabel={(data, id) => data.find((item) => item.id === id)?.balanceLabel}
        htmlId="creditAccount"
        labelText={locale['common.toAccount']}
        name="creditAccount"
        onChange={(name, value) => {
          setValue('creditAccountId', value?.[0]?.id, false);
        }}
        options={creditAccountList}
        service={accountBalServiceMapping.recurTransferCredit}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
    </>
  );
};

AccountsComponent.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  debitAccountId: PropTypes.number.isRequired,
  creditAccountId: PropTypes.number.isRequired
};

export default AccountsComponent;
